import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedHotel, getSelectedHotel, setSelectedRoom } from "../actions/hotel";
import Loading from "../components/Loading";
import { setGoogleToken, setVerifiedToken } from "../actions/integrations";
import { useHotels } from "../graphql/useHotels";
import ErrorPage from "../components/ErrorPage";
import { getUser } from "../actions/user";
import { useUserOnboarding } from "graphql/useUser";
import { setOnBoardingData, setOnBoardingUI } from "../actions/user";
import { setIdentityOrigin } from "../reducers/reducerIdentity";

import { getRoomsProjects } from "../modules/rooms/store";
import { decodeToken } from "../modules/rooms/utils";
import useAddRoom from "../hooks/useAddRoom";
import { useTranslation } from "react-i18next";
import { storagePrefix } from "localStorage";
import { useUsageStats } from "../hooks/useUsageStats";

const Start = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { trackSignUp } = useUsageStats();
    const searchParams = new URLSearchParams(window.location.search);
    // Params
    const paramCode = searchParams ? searchParams.get("code") : null;
    const paramAccessToken = searchParams ? searchParams.get("access_token") : null;
    const paramVerified = searchParams ? searchParams.get("verified") : null;
    const paramEmail = searchParams ? searchParams.get("email") : null;
    const paramEnterCode = searchParams ? searchParams.get("enterCode") === "true" : null;
    const paramRooms = searchParams ? searchParams.get("rooms") : null;
    const paramOrigin = searchParams ? searchParams.get("origin") : null;

    // Hotels
    const { getHotels, loading: loadingHotels, error, hotels: hotelsReady } = useHotels({ autoload: !!paramRooms });
    const project = useSelector(getSelectedHotel);

    // Rooms
    const { addRooms, loading: addingRooms } = useAddRoom({ project });
    const [roomsData, setRoomsData] = useState(null);

    // User
    const currentUser = useSelector(getUser);
    const user = useSelector((state) => (currentUser && state.user.data ? state.user.data[currentUser] : null));
    const roomsProjects = useSelector(getRoomsProjects);
    const bookmarks = useSelector((state) =>
        currentUser && state.user.bookmarks && state.user.bookmarks[currentUser]
            ? state.user.bookmarks[currentUser].data
            : null
    );

    const hasRooms = (hotelRef) => hotelRef && roomsProjects && roomsProjects.includes(hotelRef);
    const isBookmarked = (hotelRef) =>
        !!(hotelRef && bookmarks && bookmarks.length > 0 && bookmarks.includes(hotelRef));

    const { loading: loadingOb, error: errorOb, obResponse } = useUserOnboarding(paramEmail);

    const isAutologin = () => paramVerified || paramAccessToken;
    const isEmailOnly = !!paramEmail && !paramCode && !paramVerified && !paramAccessToken && !paramRooms;
    const hasValidObEmail = !!(obResponse?.email && obResponse.email.trim());
    const effectiveEmail = hasValidObEmail ? obResponse.email.trim() : (paramEmail || "").trim();
    const hasEmailVerified = obResponse?.emailVerified || false;
    const emailObOnComplete = !!obResponse?.onboardingComplete;

    const shouldSignin =
        !!paramCode ||
        isAutologin() ||
        (isEmailOnly && !loadingOb && !errorOb && hasValidObEmail && emailObOnComplete && hasEmailVerified) ||
        (paramRooms &&
            !!paramEmail &&
            !loadingOb &&
            !errorOb &&
            hasValidObEmail &&
            emailObOnComplete &&
            hasEmailVerified);

    const redirectURL = () => {
        let path = "";
        if (shouldSignin) {
            path += "/signin";
            if ((effectiveEmail && emailObOnComplete) || paramVerified) {
                path += "/email";
                if (effectiveEmail) {
                    if (paramEnterCode) path += "/code";
                    path += "/" + encodeURIComponent(effectiveEmail);
                }
            }
        } else {
            path += "/site";
        }
        return "/#" + path;
    };

    useEffect(() => {
        if (!paramOrigin || !obResponse || emailObOnComplete) return;
        dispatch(setIdentityOrigin(paramOrigin));
    }, [dispatch, obResponse, emailObOnComplete, paramOrigin]);

    useEffect(() => {
        if (!paramOrigin || !emailObOnComplete) return;
        trackSignUp(paramOrigin);
    }, [emailObOnComplete, paramOrigin, trackSignUp]);

    useEffect(() => {
        if (!paramEmail) return;
        try {
            localStorage.removeItem(storagePrefix + "state");
        } catch (e) {
            console.error(e);
        }
    }, [paramEmail]);

    useEffect(() => {
        if (paramCode) {
            dispatch(setGoogleToken("code=" + paramCode + "&origin=" + encodeURIComponent(window.location.origin)));
            document.location.href = redirectURL();
        }
        // eslint-disable-line react-hooks/exhaustive-deps
    }, [paramCode]);

    useEffect(() => {
        if (paramCode) return;
        if (isEmailOnly && loadingOb) return;

        if (shouldSignin || paramRooms || (isEmailOnly && !loadingOb)) {
            let awaitAddingRooms = false;

            if (paramAccessToken) {
                dispatch(setGoogleToken("access_token=" + paramAccessToken));
            } else if (paramVerified) {
                dispatch(setVerifiedToken(paramVerified));
            }
            if (isEmailOnly) {
                if (obResponse) {
                    dispatch(setOnBoardingData(obResponse));
                }

                dispatch(
                    setOnBoardingUI({
                        showDialog: true,
                        showNotif: true,
                        textDialog: t("sign-in-and-enjoy-all-our-services"),
                    })
                );
            }
            if (paramRooms) {
                if (hotelsReady) {
                    const data = decodeToken(paramRooms);
                    if (data.projectRef && project?.ref != data.projectRef) {
                        dispatch(setSelectedHotel(data.projectRef));
                    }
                    if (data?.rooms?.length > 0) {
                        awaitAddingRooms = true;
                        setRoomsData(data);
                    }
                } else {
                    getHotels();
                }

                if (hotelsReady && !awaitAddingRooms) {
                    document.location.href = redirectURL();
                }
            } else if (!awaitAddingRooms) {
                document.location.href = redirectURL();
            }
        }
        // eslint-disable-line react-hooks/exhaustive-deps
    }, [paramCode, paramRooms, hotelsReady, isEmailOnly, loadingOb, shouldSignin]);

    useEffect(() => {
        if (project && roomsData?.rooms?.length > 0) {
            if (paramEmail && loadingOb) return;

            addRooms(roomsData.rooms, {
                check: false,
                checkWiFi: false,
                silence: {
                    roomAdded: {
                        value: emailObOnComplete || paramRooms,
                        callback: (room) => {
                            dispatch(
                                setOnBoardingUI({
                                    showDialog: false,
                                    room: room?.number,
                                    showNotif: true,
                                    roomFromParams: !!paramRooms && !shouldSignin,
                                })
                            );

                            if (!!paramRooms && !shouldSignin) {
                                sessionStorage.setItem("roomAddedFromParams", true);
                            }
                        },
                    },
                },
            }).then((success) => {
                if (success && roomsData.selected) {
                    dispatch(setSelectedRoom(roomsData.selected));
                    console.log(obResponse);
                    if (obResponse) {
                        dispatch(setOnBoardingData(obResponse));
                    }
                }
                setTimeout(() => {
                    document.location.href = redirectURL();
                }, 1000);
            });
        }
        // eslint-disable-line react-hooks/exhaustive-deps
    }, [roomsData, project?.ref, errorOb, obResponse, loadingOb]);
    if (error) {
        return <ErrorPage type="server">{error}</ErrorPage>;
    }

    if (paramCode || shouldSignin || paramRooms || addingRooms || loadingHotels || loadingOb) {
        return <Loading />;
    }

    if (project && !hasRooms(project?.ref) && !isBookmarked(project?.ref)) {
        dispatch(setSelectedHotel(null));
    }

    return <Redirect push to={project || user ? "/site" : "/signin"} />;
};

export default Start;
